import { useEffect, useState } from "react";
import { couponService } from "../../../Api/Services/couponService";
import { Coupon } from "../../../Models/Coupon";
import { formatDateToBR } from "../../../Utils/DataFormatters";
import { FaBoxOpen, FaCheckCircle, FaEdit, FaPlus, FaTimesCircle, FaTrash } from "react-icons/fa";
import { toast } from "react-toastify";
import SmartNavbar from "../../../Components/SmartNavbar/SmartNavbar";
import PageTitle from "../../../Components/PageTitle/PageTitle";
import FloatingInput from "../../../Components/FloatingInput/FloatingInput";
import Button from "../../../Components/Button/Button";

const AdminCouponPage = () => {
  const [coupons, setCoupons] = useState<Coupon[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [showCouponForm, setShowCouponForm] = useState<boolean>(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [newCode, setNewCode] = useState("");
  const [newDiscountPercentage, setNewDiscountPercentage] = useState<number>(0);
  const [newIsActive, setNewIsActive] = useState<boolean>(true);

  useEffect(() => {
    load();
  }, []);

  const load = async () => {
    try {
      setIsLoading(true);
      const data = await couponService.getAll();
      setCoupons(data);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    if (!newCode.trim()) {
      return;
    }

    if (editId) {
      await couponService.update({ 
        id: editId, 
        code: newCode, 
        discountPercentage: newDiscountPercentage,
        isActive: newIsActive
      });
      toast.success("Cupom atualizado com sucesso!");
    } else {
      await couponService.create({ 
        code: newCode, 
        discountPercentage: newDiscountPercentage 
      });
      toast.success("Cupom adicionado com sucesso!");
    }

    setNewCode("");
    setNewDiscountPercentage(0);
    setNewIsActive(true);
    setEditId(null);
    setShowCouponForm(false);
    load();
  };

  const handleDelete = async (id: string) => {
    try {
      const deleted = await couponService.delete(id);

      if (deleted) {
        toast.success("Cupom removido com sucesso!");
        load();
      }
    } catch (err: any) {
      toast.error(err.response?.data || "Não foi possível remover o cupom, tente novamente mais tarde.");
    }
  };

  const handleToggleActive = async (coupon: Coupon) => {
    const next = !coupon.isActive;
    try {
      const updated = await couponService.updateStatus(coupon.id, next);
      if (updated) {
        toast.success(`Cupom ${next ? "ativado" : "desativado"} com sucesso!`);
        setCoupons((prev) =>
          prev.map((c) => (c.id === coupon.id ? { ...c, isActive: next } : c))
        );
      }
    } catch (err: any) {
      toast.error(err.response?.data || "Não foi possível atualizar o status do cupom.");
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <SmartNavbar />
      <div className="p-4 sm:p-14 max-w-full flex-1 flex flex-col">
        <PageTitle className="my-6 sm:my-0 sm:mb-8">{"CUPONS"}</PageTitle>

        {showCouponForm ? (
          <div className="mb-4 flex flex-col sm:flex-row gap-2">
            <FloatingInput
              label={"Código do cupom"}
              name="newCode"
              type="text"
              value={newCode}
              onChange={(e) => setNewCode(e.target.value)}
              required
            />

            <FloatingInput
              label={"Desconto (%)"}
              name="newDiscountPercentage"
              type="number"
              value={newDiscountPercentage}
              min={0}
              max={100}
              step={0.01}
              onChange={(e) => setNewDiscountPercentage(parseFloat(e.target.value))}
              required
            />
            
            {editId && (
              <label className="flex items-center gap-2 p-3 border border-gray-300 rounded">
                <input
                  type="checkbox"
                  checked={newIsActive}
                  onChange={(e) => setNewIsActive(e.target.checked)}
                />
                {"Ativo"}
              </label>
            )}

            <div className="flex gap-2">
              <Button variant="secondary"
                onClick={() => {
                  setEditId(null);
                  setNewCode("");
                  setNewDiscountPercentage(0);
                  setNewIsActive(true);
                  setShowCouponForm(false);
                }}
                className="w-full sm:w-32 px-4 py-2"
              >
                {"CANCELAR"}
              </Button>

              <Button
                onClick={handleSave}
                className="w-full sm:w-32 px-4 py-2"
              >
                {editId ? "SALVAR" : "ADICIONAR"}
              </Button>
            </div>
          </div>
        ) : (
          <button 
            className="uppercase sm:normal-case w-full sm:w-auto flex items-center justify-center sm:justify-start gap-1.5 sm:gap-1 py-2 sm:py-0 rounded sm:rounded-none bg-primary-600 sm:bg-transparent text-white sm:text-primary-600 text-sm sm:text-base font-semibold sm:font-normal my-2 hover:bg-primary-700 sm:hover:bg-transparent sm:hover:text-primary-700"
            onClick={() => setShowCouponForm(true)}
          >
            <FaPlus /> {"Adicionar Cupom"}
          </button>
        )}

        {isLoading ? (
          <div className="mt-6 w-full space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="animate-pulse bg-white border rounded p-4 shadow-sm">
                <div className="h-4 bg-gray-200 rounded w-1/3 mb-3"></div>
                <div className="h-3 bg-gray-200 rounded w-1/2"></div>
              </div>
            ))}
          </div>
        ) : coupons.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center gap-3 text-gray-500">
            <FaBoxOpen className="text-3xl" />
            <p className="text-center text-sm sm:text-base">{"Nenhum cupom encontrado"}</p>
          </div>
        ) : (
          <>
            {/* MOBILE CARDS */}
            <div className="flex flex-col gap-4 sm:hidden">
              {coupons.map((coupon, index) => (
                <div key={coupon.id} className="border rounded p-4 shadow-sm bg-white">
                  <h2 className="font-semibold mb-2">
                    {"Cupom"} {index + 1}
                  </h2>
                  <p>
                    <span className="font-medium">{"Código"}:</span> {coupon.code}
                  </p>
                  <p>
                    <span className="font-medium">{"Desconto"}:</span> {coupon.discountPercentage}%
                  </p>
                  <p>
                    <span className="font-medium">{"Ativo"}:</span>{" "}
                    <button
                      className="inline-flex items-center gap-2"
                      onClick={() => handleToggleActive(coupon)}
                    >
                      {coupon.isActive ? (
                        <FaCheckCircle className="text-green-600" />
                      ) : (
                        <FaTimesCircle className="text-red-600" />
                      )}
                      <span className={coupon.isActive ? "text-green-600" : "text-red-600"}>
                        {coupon.isActive ? "Sim" : "Não"}
                      </span>
                    </button>
                  </p>
                  <p>
                    <span className="font-medium">{"Data de criação"}:</span> {formatDateToBR(coupon.createdAt)}
                  </p>
                  <div className="flex gap-2 mt-3">
                    <Button
                      className="flex-1 flex items-center justify-center gap-1.5 py-2 text-sm"
                      onClick={() => {
                        setEditId(coupon.id);
                        setNewCode(coupon.code);
                        setNewDiscountPercentage(coupon.discountPercentage);
                        setNewIsActive(coupon.isActive);
                        setShowCouponForm(true);
                      }}
                    >
                      <FaEdit /> {"Editar"}
                    </Button>
                    <Button
                      className="flex-1 flex items-center justify-center gap-1.5 py-2 text-sm"
                      onClick={() => handleDelete(coupon.id)}
                    >
                      <FaTrash /> {"Excluir"}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
            
            {/* DESKTOP TABLE */}
            <table className="hidden sm:table w-full md:max-w-6xl border text-left mb-20">
              <thead>
                <tr className="bg-primary-120">
                  <th className="border p-3 text-center">{"Código"}</th>
                  <th className="border p-3 text-center">{"Desconto"}</th>
                  <th className="border p-3 text-center">{"Ativo"}</th>
                  <th className="border p-3 text-center">{"Ações"}</th>
                </tr>
              </thead>
              <tbody>
                {coupons.map((c: Coupon) => (
                  <tr key={c.id} className="border-t">
                    <td className="border p-3 text-center">
                      {c.code}
                    </td>
                    <td className="border p-3 text-center">
                      {c.discountPercentage}%
                    </td>
                    <td className="border p-3">
                      <div className="flex justify-center items-center">
                        <button
                          className="flex items-center gap-2"
                          onClick={() => handleToggleActive(c)}
                          title={c.isActive ? "Desativar" : "Ativar"}
                        >
                          {c.isActive ? (
                            <FaCheckCircle className="text-green-600" />
                          ) : (
                            <FaTimesCircle className="text-red-600" />
                          )}
                        </button>
                      </div>
                    </td>
                    <td className="border p-3">
                      <div className="flex justify-center items-center gap-3">
                        <button
                          className="text-primary-600"
                          onClick={() => {
                            setEditId(c.id);
                            setNewCode(c.code);
                            setNewDiscountPercentage(c.discountPercentage);
                            setNewIsActive(c.isActive);
                            setShowCouponForm(true);
                          }}
                        >
                          <FaEdit />
                        </button>
                        <button
                          className="text-primary-600"
                          onClick={() => handleDelete(c.id)}
                        >
                          <FaTrash />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
      </div>
    </div>
  );
};

export default AdminCouponPage;
